import { useMutation } from '@apollo/client';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { COMPLETE_INVESTMENT } from '@/apollo/schemas/investments/completeInvestment';
import { EXECUTE_INVESTMENT_PAYMENT } from '@/apollo/schemas/investments/executeInvestmentPayment';
import { UPDATE_INVESTMENT_STATUS } from '@/apollo/schemas/investments/updateInvestmentStatus';
import AccreditationReviewPanel, {
  EnvelopeLink,
} from '@/components/customers/AccreditationReviewPanel';
import { ComplianceCell } from '@/components/investments/ComplianceCell';
import {
  CopyValue,
  InvestmentStatusPill,
  TInvestment,
} from '@/components/investments/InvestmentsTableColumns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { useToast } from '@/hooks/use-toast';
import { chainLabel } from '@/utils/chains';
import { RESERVATION_TONE, reservationSummary } from '@/utils/reservation';

type LocationState = { investment?: TInvestment };

const formatUsd = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(amount);

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '-';

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="min-w-0">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <div className="mt-1 text-sm">{children}</div>
  </div>
);

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-lg border bg-card p-4 shadow-sm">
    <div className="mb-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    {children}
  </div>
);

const REGISTRY_STYLES: Record<string, string> = {
  REGISTERED: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  PENDING: 'border-amber-200 bg-amber-50 text-amber-700',
  SENDING: 'border-amber-200 bg-amber-50 text-amber-700',
  FAILED: 'border-red-200 bg-red-50 text-red-700',
};

/**
 * One investment: what it is, whether it can be minted, its documents and the investor's
 * accreditation (with Approve/Deny), and the payment actions. Opened from the arrow on the
 * investments list, which passes the row along.
 */
const InvestmentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { investmentId } = useParams();
  const { session } = useAuth();
  const { toast } = useToast();

  const passed = (location.state as LocationState | null)?.investment;
  // Status changes from the actions below are applied here, so the page stays current
  // without a list query to refetch.
  const [investment, setInvestment] = useState<TInvestment | undefined>(passed);

  const headers = {
    identity: session
      ? session.getIdToken().getJwtToken()
      : localStorage.getItem('idToken'),
  };

  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(refetchTimeoutRef.current), []);

  const setStatus = (status?: string) =>
    status &&
    setInvestment((current) => (current ? { ...current, status } : current));

  const [confirmCancel, setConfirmCancel] = useState(false);

  // The API answers a refused change (e.g. 409 for a closed investment) with a status code in
  // the payload, not a GraphQL error, so both are checked.
  const [updateInvestmentStatus, { loading: updating }] = useMutation(
    UPDATE_INVESTMENT_STATUS,
    { context: { headers } },
  );

  const changeStatus = async (
    status: 'USER_PAY_DISPUTE' | 'CANCELLED',
    done: { title: string; description: string },
    failTitle: string,
  ) => {
    try {
      const { data } = await updateInvestmentStatus({
        variables: { input: { id: investment?.id, status } },
      });
      const res = data?.updateInvestmentStatus;
      if (res?.statusCode !== 200) {
        toast({
          variant: 'destructive',
          title: failTitle,
          description: res?.message ?? 'The status was not changed.',
        });
        return false;
      }
      setStatus(res.investment?.status ?? status);
      toast({ variant: 'success', ...done });
      return true;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: failTitle,
        description: (error as Error).message,
      });
      return false;
    }
  };

  const [executeInvestmentPayment, { loading: approving }] = useMutation(
    EXECUTE_INVESTMENT_PAYMENT,
    {
      context: { headers },
      onCompleted: (res) => {
        const result = res?.executeInvestmentPayment;
        if (!result?.success || result.statusCode !== 200) {
          toast({
            variant: 'destructive',
            title: 'Failed to approve payment.',
            description: result?.message ?? 'The payment was not executed.',
          });
          return;
        }
        // Applied here so Approve does not come back while the tokens are delivered: a
        // payment with a tx hash completed on the spot, otherwise the mint is under way
        // (PROCESSING) until the Chainlink webhook marks it COMPLETED.
        setStatus(result.data?.txHash ? 'COMPLETED' : 'PROCESSING');
        toast({
          variant: 'success',
          title: 'Payment approved',
          description: result.data?.txHash
            ? 'The payment was executed and the investment is completed.'
            : 'The tokens are being delivered. The investment is completed once the chain confirms; reopen it from the list in a minute.',
        });
      },
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: 'Failed to approve payment.',
          description: error.message,
        }),
    },
  );

  const [completeInvestment, { loading: completing }] = useMutation(
    COMPLETE_INVESTMENT,
    {
      context: { headers },
      onCompleted: (res) => {
        const result = res?.completeInvestment;
        if (result?.statusCode && result.statusCode !== 200) {
          toast({
            variant: 'destructive',
            title: 'Failed to complete investment.',
            description: result.message,
          });
          return;
        }
        setStatus(result?.investment?.status ?? 'COMPLETED');
        toast({
          variant: 'success',
          title: 'Investment completed',
          description: 'The investment has been marked as completed.',
        });
      },
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: 'Failed to complete investment.',
          description: error.message,
        }),
    },
  );

  const handleBack = () => navigate('/admin/investments');

  if (!investment) {
    return (
      <div className="space-y-4 px-4 py-6">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Investments
        </Button>
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6">
          <h2 className="text-lg font-semibold text-destructive">
            No investment data
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This page is opened from the investments list. Go back and open
            investment <span className="font-mono">{investmentId ?? ''}</span>{' '}
            again.
          </p>
        </div>
      </div>
    );
  }

  const { status, compliance } = investment;
  // COMPLETED and CANCELLED are final: every action is off and the backend refuses changes.
  const isClosed = status === 'COMPLETED' || status === 'CANCELLED';
  const isMinting = status === 'PROCESSING';
  const showApprove =
    !isClosed && (status === 'USER_PAID' || status === 'USER_PAY_DISPUTE');
  const disableDispute = isClosed || status === 'USER_PAY_DISPUTE';
  const busy = updating || approving || completing;

  return (
    <div className="space-y-6 px-4 py-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">Investment</h1>
            <InvestmentStatusPill status={status} />
          </div>
          <CopyValue value={investment.id} head={36} />
        </div>
      </div>

      <Section title="Overview">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Customer">
            <div className="flex flex-col">
              <span className="font-medium">
                {investment.customer?.name ?? '-'}
              </span>
              <span className="text-muted-foreground">
                {investment.customer?.email ?? '-'}
              </span>
              {investment.customer?.id && (
                <Link
                  to={`/admin/customers/${investment.customer.id}`}
                  className="text-xs text-primary hover:underline"
                >
                  Open customer
                </Link>
              )}
            </div>
          </Field>
          <Field label="Wallet">
            <CopyValue
              value={investment.customerWalletAddress}
              head={10}
              tail={8}
            />
          </Field>
          <Field label="Asset">
            <div className="flex flex-col">
              <span className="flex flex-wrap items-center gap-2 font-medium">
                {investment.asset?.name ?? '-'}
                {investment.asset?.chain && (
                  <Badge variant="outline" className="text-[10px]">
                    {chainLabel(investment.asset.chain)}
                  </Badge>
                )}
              </span>
              <span className="text-xs text-muted-foreground">
                {investment.asset?.type ?? ''}
              </span>
            </div>
          </Field>
          <Field label="Amount">
            <span className="font-semibold">
              {formatUsd(Number(investment.totalAmount ?? 0))}
            </span>
          </Field>
          <Field label="Payment type">
            {investment.isCrypto === false ? 'Fiat' : 'Crypto'}
          </Field>
          <Field label="Tokens">
            {investment.tokensAllocated != null
              ? `${investment.tokensAllocated.toLocaleString()} allocated`
              : investment.projectedNumberOfTokens != null
                ? `${investment.projectedNumberOfTokens.toLocaleString()} projected`
                : '-'}
          </Field>
          <Field label="Payment reference">
            {investment.paymentReference ?? '-'}
          </Field>
          <Field label="Transaction">
            {investment.transactionHash ? (
              investment.explorerUrl ? (
                <a
                  href={investment.explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                  title={investment.transactionHash}
                >
                  {investment.transactionHash.slice(0, 10)}…
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <CopyValue value={investment.transactionHash} />
              )
            ) : (
              '-'
            )}
          </Field>
          <Field label="Created">{formatDate(investment.createdAt)}</Field>
          <Field label="Updated">{formatDate(investment.updatedAt)}</Field>
          <Field label="Completed">{formatDate(investment.completedAt)}</Field>
          <Field label="Token reservation">
            {(() => {
              const reservation = reservationSummary(investment);
              if (!reservation) {
                return (
                  <span className="text-muted-foreground">
                    {investment.status === 'COMPLETED' &&
                    investment.reservationStatus
                      ? 'Tokens delivered'
                      : 'Not reserved'}
                  </span>
                );
              }
              return (
                <div className="flex flex-col gap-1">
                  <span
                    className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-[11px] font-medium ${RESERVATION_TONE[reservation.tone]}`}
                  >
                    {reservation.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {reservation.detail}
                  </span>
                </div>
              );
            })()}
          </Field>
        </div>
      </Section>

      <Section
        title="Mint ready"
        description="Whether tokens can be delivered to this investor's wallet right now."
      >
        <ComplianceCell investment={investment} />
      </Section>

      <Section
        title="Actions"
        description={
          isClosed
            ? undefined
            : 'Complete and Cancel both close the investment for good.'
        }
      >
        {isClosed && (
          <p className="mb-3 text-sm text-muted-foreground">
            This investment is {status.toLowerCase()}. It is closed and can no
            longer be changed.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {investment.isCrypto === false && (
            <>
              {showApprove && (
                <Button
                  size="sm"
                  disabled={busy}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() =>
                    executeInvestmentPayment({
                      variables: { investmentId: investment.id },
                    })
                  }
                >
                  {approving && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {approving ? 'Processing…' : 'Approve'}
                </Button>
              )}
              {isMinting && (
                <Button
                  size="sm"
                  disabled
                  title="Tokens are being delivered. The investment is completed once the chain confirms."
                  className="bg-emerald-600 text-white"
                >
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                disabled={disableDispute || busy}
                onClick={() =>
                  changeStatus(
                    'USER_PAY_DISPUTE',
                    {
                      title: 'Investment disputed',
                      description:
                        'The investment status was updated to disputed.',
                    },
                    'Failed to dispute investment.',
                  )
                }
              >
                Dispute
              </Button>
              {/* Compliance never disables Complete: the on-chain policy is the real gate,
                  and our view of compliance can lag. The reason is shown on hover. */}
              <Button
                size="sm"
                disabled={isClosed || busy}
                title={
                  compliance?.applicable && !compliance.ready
                    ? (compliance.reason ?? undefined)
                    : undefined
                }
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={() =>
                  completeInvestment({ variables: { id: investment.id } })
                }
              >
                Complete
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={isClosed || isMinting || busy}
            title={
              isMinting
                ? 'Tokens are being minted for this investment. Wait for it to finish before cancelling.'
                : undefined
            }
            className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
            onClick={() => setConfirmCancel(true)}
          >
            Cancel investment
          </Button>
        </div>
      </Section>

      <Dialog
        open={confirmCancel}
        onOpenChange={(open) => !open && setConfirmCancel(false)}
      >
        <DialogContent className="sm:max-w-[440px] bg-white dark:bg-foreground dark:text-white">
          <DialogHeader>
            <DialogTitle>Cancel this investment?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The investment of{' '}
            <span className="font-medium text-foreground">
              {formatUsd(Number(investment.totalAmount ?? 0))}
            </span>{' '}
            by{' '}
            <span className="font-medium text-foreground">
              {investment.customer?.name || investment.customer?.email}
            </span>{' '}
            is closed as CANCELLED. This is final: it cannot be reopened or
            completed afterwards. Nothing else changes for the investor (their
            KYC, wallet and accreditation stay as they are).
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmCancel(false)}
              disabled={updating}
            >
              Keep investment
            </Button>
            <Button
              type="button"
              disabled={updating}
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={async () => {
                const ok = await changeStatus(
                  'CANCELLED',
                  {
                    title: 'Investment cancelled',
                    description: 'The investment is closed as cancelled.',
                  },
                  'Failed to cancel investment.',
                );
                if (ok) setConfirmCancel(false);
              }}
            >
              Cancel investment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Section
        title="This investment's documents"
        description="The subscription agreement and the on-chain record for this investment."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Subscription agreement">
            <EnvelopeLink envelopeId={investment.docusignEnvelopeId} />
          </Field>
          <Field label="On-chain record">
            {investment.registryStatus ? (
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${REGISTRY_STYLES[investment.registryStatus] ?? 'border-gray-200 bg-gray-50 text-gray-600'}`}
                >
                  {investment.registryStatus}
                </span>
                {investment.registryTxHash && (
                  <a
                    href={`https://testnet.snowtrace.io/tx/${investment.registryTxHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    View transaction <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">Not written yet</span>
            )}
          </Field>
        </div>
      </Section>

      {investment.customer?.id && (
        <AccreditationReviewPanel
          customerId={investment.customer.id}
          customerName={investment.customer.name || investment.customer.email}
        />
      )}
    </div>
  );
};

export default InvestmentDetail;
